import { DimensionSource, IntervalJSON } from '@highimpact-frontend/api-types';
import { ErrorStatus } from '@highimpact/dashboard-ui';
import { WidgetSourceSelector } from '@highimpact/source-selector';
import { TimeRangeUnionForm } from '@highimpact/time-interval-selector';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MigratedPlotFormProps } from '../../../../forms/formHandlerShared';
import {
  ensureConfiguredHistogram,
  getIntervals,
  isValid,
  setElementIndependentSourcesFromResolvedSources,
  setIntervals,
} from '../../../../state/dashboard-element-operations';
import { getDefaultIndependentDimensionKeys } from '../shared/buildWidgetDataSourceRequest';
import {
  buildIndependentDimensionSelectionFromElement,
  dimensionSelectionToAxisSources,
  resolveIndependentDimensionSelectionFromCatalog,
  syncIndependentDimensionSelectionFromCatalog,
} from '../shared/independentDimensionSelection';
import {
  PlotConfigFormDivider,
  PlotConfigFormShell,
  PlotConfigInvalidStatus,
  PlotConfigSubmitButton,
} from '../shared/plotConfigFormLayout';
import { areAxisSourceSelectionsEqual } from '../shared/resolveSourcesFromCatalog';
import { useWidgetSourceSelection } from '../shared/useWidgetSourceSelection';

const HISTOGRAM_DIMENSION_KEYS =
  getDefaultIndependentDimensionKeys('histogram');
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const HISTOGRAM_DIMENSION_KEY = HISTOGRAM_DIMENSION_KEYS[0]!;

function HistogramPlotForm({
  config,
  project_id,
  site_id,
  campaigns,
  onSubmit,
  onChange,
}: MigratedPlotFormProps) {
  const scope = useMemo(() => ({ project_id, site_id }), [project_id, site_id]);
  const { loading, error, data } = useWidgetSourceSelection({
    widget: 'histogram',
    scope,
  });

  const initialDimensionSelection = useMemo(
    () =>
      buildIndependentDimensionSelectionFromElement(
        data && data.response_type === 'dimensions' ? data : null,
        config,
        HISTOGRAM_DIMENSION_KEYS,
        (sources) => ({ [HISTOGRAM_DIMENSION_KEY]: [...sources] })
      ),
    [config, data]
  );

  const [dimensionSelection, setDimensionSelection] = useState<
    Record<string, DimensionSource[]>
  >(initialDimensionSelection);
  const dimensionSelectionRef = useRef(dimensionSelection);
  dimensionSelectionRef.current = dimensionSelection;

  const [submitCount, setSubmitCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [validation, setValidation] = useState(isValid(config));

  useEffect(() => {
    setDimensionSelection(initialDimensionSelection);
  }, [initialDimensionSelection]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setDimensionSelection((previousSelection) => {
      const syncedSelection = syncIndependentDimensionSelectionFromCatalog(
        data,
        HISTOGRAM_DIMENSION_KEYS,
        previousSelection
      );

      return areAxisSourceSelectionsEqual(
        dimensionSelectionToAxisSources(previousSelection),
        dimensionSelectionToAxisSources(syncedSelection)
      )
        ? previousSelection
        : syncedSelection;
    });
  }, [data]);

  const applyDimensionSelection = useCallback(
    (nextSelection: Record<string, DimensionSource[]>) => {
      const resolvedSelection = resolveIndependentDimensionSelectionFromCatalog(
        data,
        nextSelection,
        HISTOGRAM_DIMENSION_KEYS
      );
      const newConfig = setElementIndependentSourcesFromResolvedSources(
        resolvedSelection,
        HISTOGRAM_DIMENSION_KEYS,
        ensureConfiguredHistogram(config)
      );
      setValidation(isValid(newConfig));
      onChange(newConfig);
    },
    [config, data, onChange]
  );

  const handleIntervalsUpdate = (intervals: IntervalJSON[]) => {
    const newConfig = setIntervals(
      intervals,
      ensureConfiguredHistogram(config)
    );
    setValidation(isValid(newConfig));
    onChange(newConfig);
  };

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setSubmitCount(submitCount + 1);
    if (validation[0]) {
      setIsSending(true);
      onSubmit(config).finally(() => setIsSending(false));
    }
  };

  const dataSourceErrors = validation?.[1]?.dataSource ?? [];
  const dataSourceHasError: ErrorStatus =
    submitCount > 0
      ? { tag: 'Submited', errors: dataSourceErrors }
      : { tag: 'Not submitted' };

  return (
    <PlotConfigFormShell onSubmit={handleSubmit}>
      <WidgetSourceSelector
        data={data}
        loading={loading}
        error={error}
        initialIndependentSelection={dimensionSelection}
        onIndependentDimensionSelect={(_dimensionKey, selected) => {
          const nextSelection = {
            ...dimensionSelectionRef.current,
            [HISTOGRAM_DIMENSION_KEY]: selected,
          };
          setDimensionSelection(nextSelection);
          applyDimensionSelection(nextSelection);
        }}
        matchControlPanelStyle
      />

      <PlotConfigInvalidStatus error={dataSourceHasError} />

      <PlotConfigFormDivider />

      <TimeRangeUnionForm
        onChange={handleIntervalsUpdate}
        campaigns={campaigns}
        defaultIntervals={getIntervals(config)}
        matchControlPanelStyle
      />

      <PlotConfigSubmitButton
        isSending={isSending}
        loading={loading}
        disabled={
          dataSourceHasError.tag === 'Submited' &&
          dataSourceHasError.errors.length > 0
        }
      />
    </PlotConfigFormShell>
  );
}

export default HistogramPlotForm;
